/**
 * Nex PiP 3D prototype bridge — v1.2.1 build 16.
 *
 * Talks to the iOS `NexPip3D` Capacitor plugin (a brand-new AVKit PiP
 * surface that is fully isolated from the legacy `NexPip` plugin and
 * from the OCR pipeline). Anything imported from this file is safe to
 * call from anywhere in the React tree without dragging in scan logic.
 *
 * Native counterpart: ios/App/App/Plugins/NexPip3D/NexPip3D.swift
 */

import { getPlatform } from "./platform";

/**
 * Phases that the iOS HUD knows how to render with a built-in copy
 * string. Any other string is allowed too — pass `message` to override.
 */
export type NexPip3DPhase =
  | "idle"
  | "online"
  | "open_pogo"
  | "hold_box"
  | "reading"
  | "scanning"
  | "captured_threshold_reached"
  | "finished"
  | "no_box"
  | "paused";

export interface NexPip3DStatus {
  phase: NexPip3DPhase | string;
  captured?: number;
  /** Default 12. Set to 1 to suppress the progress bar. */
  target?: number;
  /** Override the body line. If omitted, native renders the default for `phase`. */
  message?: string;
  /** Optional sub-line under the body, e.g. "Box page 2 / 4". */
  detail?: string;
  /**
   * 2026-08-29: HUD layout. "split" = the naming-session layout (message
   * HUGE in the top half, detail centred in the bottom half); "standard"
   * restores the scan readout. Sticky on the native side: omit to keep the
   * current mode. Older binaries ignore it.
   */
  layout?: "split" | "standard";
  /**
   * 2026-09-07: HUD shape. iOS snaps the PiP window to a corner and offers
   * no positioning API, but the window's SHAPE follows the frame we render:
   * "portrait" renders a tall 320x568 buffer for a Pokémon scan (species /
   * My-Pokémon page) so the top-right column clears the CP band instead of
   * covering it; "landscape" is the 540x304 card the Pokédex grid scan uses.
   * Sticky on the native side: omit to keep the current shape. Older
   * binaries ignore it; Android's overlay reflows on its own.
   */
  orientation?: "landscape" | "portrait";
  /**
   * 2026-08-30: split layout's subline, drawn under the big name ("CP 1500
   * · GL 100%"). Sticky: absent leaves it, "" clears it. Split layout only.
   */
  splitSub?: string;
  /**
   * v1.3.2(9): three top-row readiness circles (slots 0/1/2).
   * v1.3.2(10): per-slot palette is "green" / "yellow" / "orange".
   * "red" is NO LONGER a valid circle state — it is reserved for the
   * top-level bubble only (hard failure). When present, the native HUD
   * renders three small circles ABOVE the body line and SUPPRESSES the
   * "N/captured" progress chip. The user's mental model: green here
   * means the top row is safe to scroll off-screen.
   */
  circles?: [
    "orange" | "yellow" | "green",
    "orange" | "yellow" | "green",
    "orange" | "yellow" | "green",
  ];
  /**
   * v1.3.2(9): directional arrow. "scroll-down" (green chevron), "hold"
   * (yellow pause bars), "back" (red back-chevron), or "none" (no
   * graphic, fallback to the bubble dot alone).
   */
  arrow?: "scroll-down" | "hold" | "back" | "none";
  /**
   * v1.3.2(9–10): bubble colour for the panel border + glyph tint.
   * b10 added "orange" for PAUSE; red stays reserved for hard failures
   * such as no broadcast / wrong screen / no frames at all.
   * When omitted, native infers from `phase`.
   */
  bubble?: "green" | "yellow" | "orange" | "red";
  /**
   * PR AA: up to 12 recently-added dex numbers. The native HUD renders these
   * as a 3-row x 4-column grid of zero-padded #000 dex numbers directly under
   * the message + detail lines, so the user can watch OCR progress while
   * Pokemon GO is in focus. The native side fills the grid from the top-left
   * in plain array order; fewer than 12 entries leave the trailing slots blank.
   * When omitted the native side leaves the previous grid untouched.
   *
   * PR-E30 (2026-06-22): 4 numbers per row mirrors the Pokédex's 4-wide rows,
   * so a freshly-registered full row of 4 lands as one clean PiP row.
   *
   * 2026-09-24: POSITIONAL. Four slots per Pokédex row, rows in dex order;
   * 0 is a blank column the HUD draws as nothing, so each number sits under
   * the column it occupies in the Pokédex (dexRecentNumbersForPip()).
   */
  recentDexNumbers?: number[];
  /**
   * 2026-08-31 split layout: the My-Pokémon HUD's left-column fact lines
   * (last entry larger) while the target visual owns the right half. Sticky
   * like detailVariantSlots: absent leaves, empty clears.
   */
  heroLines?: string[];
  /**
   * 2026-08-31: per-cell kind aligned index-for-index with recentDexNumbers.
   * "added" renders a light chip with dark text (newly registered); "already"
   * a dark green chip with white text (recognised, but the dex already had
   * it — the calm "yes, I saw it" answer to a re-scan). 2026-09-24: "seen"
   * an orange-red chip (a silhouette: seen, not caught) and "missing" a red
   * chip (a blank card: not caught); "" under a blank slot. Same sticky
   * contract as recentDexNumbers: absent leaves the grid, empty clears.
   */
  recentDexKinds?: string[];
  /**
   * 2026-08-21: top-1% league ball for the My-Pokémon detail HUD. "great" /
   * "ultra" / "master" draws that league's ball in the panel's top-right
   * corner; "" clears it; absent leaves the current badge (sticky, like the
   * detail fields). Forwarded by setDexPipPhase in nexPip.ts.
   */
  leagueBadge?: string;
  /**
   * 2026-08-26: hero band for the My-Pokémon detail HUD, drawn between the
   * headline and the fact grid at a larger size than the grid cells.
   * pvpLine carries the league percents; evoLine the evolution-potential
   * callout (brand green). "" clears each; absent leaves them (sticky).
   */
  pvpLine?: string;
  evoLine?: string;
  /**
   * 2026-08-30: the TARGET VISUAL for the My-Pokémon detail HUD — what this
   * IV triple could become, drawn as a picture instead of words: the target
   * form's art (fetched + cached natively from targetSpriteUrl), its
   * league's ball overlapping the sprite's bottom-centre, and an oversized
   * rank beside it. Sticky like the other detail fields: "" / 0 clears,
   * absent leaves. targetName captions an EVOLUTION target; "" for none.
   */
  targetSpriteUrl?: string;
  targetLeague?: string;
  targetRank?: number;
  targetName?: string;
  /** Pre-formatted percent for the target's caption ("97.6%"); "" clears. */
  targetPercent?: string;
  /**
   * 2026-09-03: every league in which the target has EXACTLY the standing
   * the pick was made on ("great" / "ultra" / "master", GL-first). A species
   * that cannot reach a league's CP cap scores the same in that league and
   * the next one up, so the HUD draws one ball per entry under the art and
   * states the percent + rank once. Always contains targetLeague when set.
   * Sticky like the other target fields: absent leaves, [] clears (the
   * renderer then falls back to a single targetLeague ball).
   */
  targetLeagues?: string[];
  /**
   * 2026-09-03: the moves read off the page, shown as pills in the split
   * layout's left column (fast move first). Sticky: absent leaves, []
   * clears.
   */
  movePills?: string[];
  /**
   * 2026-09-22 (Android HUD): the raw IV percent, colouring the tab and
   * badge border by GO's appraisal bands. Sticky: absent leaves, 0 clears.
   */
  ivPercent?: number;
  /**
   * 2026-09-22 (Android HUD): which face the right-hand badge shows, "ivs"
   * (the destiny picture) or "moves" (registered vs recommended moves).
   * Sticky: absent leaves, "" clears (destiny).
   */
  hudFocus?: string;
  /**
   * 2026-09-22 (Android HUD): the recommended moveset for the target in its
   * league, clipped like movePills. Sticky: absent leaves, [] clears.
   */
  moveRecs?: string[];
  /**
   * 2026-09-22 (Android HUD): a top-5 showcase standing for this Pokémon,
   * shown briefly as a strip under the badge. Sticky: absent leaves,
   * rank 0 / "" clears.
   */
  showcaseRank?: number;
  showcaseLabel?: string;
  showcaseScore?: string;
  /**
   * 2026-09-22 (Android HUD): a WILD ENCOUNTER read off the banner, shown
   * as the IV percent range the catch could be. Sticky: absent leaves,
   * encounterCp 0 clears the lot.
   */
  encounterName?: string;
  encounterCp?: number;
  encounterIvMin?: number;
  encounterIvMax?: number;
  encounterLevelMin?: number;
  encounterLevelMax?: number;
  encounterSpreads?: number;
}

export interface NexPip3DDiagnostics {
  isSupported: boolean;
  isPossible: boolean;
  isActive: boolean;
  controllerExists: boolean;
  audioSessionActive: boolean;
  framesRendered: number;
  /** Unix seconds; 0 if no frame has been enqueued yet. */
  lastFrameAt: number;
  /** Unix seconds; 0 if start() has not been called. */
  lastStartAttemptAt: number;
  /** Last delegate / KVO event name, e.g. "willStartPiP". */
  lastDelegateEvent: string;
  phase: string;
  message: string;
  captured: number;
  target: number;
  displayLinkActive: boolean;
  hostViewAttached: boolean;
  hostViewWidth: number;
  hostViewHeight: number;
  layerReady: boolean;
  layerStatus: number;
  autoInlineEnabled: boolean;
  lastError: string | null;
  /** v1.3.2 b1: last frame-render error (FigSampleBufferSerialization etc.). */
  lastRenderError?: string | null;
  /** v1.3.2 b1: which native plugin produced this snapshot. */
  pluginName?: string;
  /** v1.3.2 b1: "hud" or "smoke". */
  rendererMode?: string;
  /** v1.3.2 b1: current smoke-test label, if rendererMode === "smoke". */
  smokeLabel?: string;
  /** v1.3.2 b2: which native rendering backend is feeding PiP — "sampleBuffer" (current) or future "playerAsset" / "fallback". */
  rendererBackend?: string;
  /** v1.3.2 b2: whether the IOSurface-backed pixel buffer pool was constructed. */
  pixelBufferPoolReady?: boolean;
  /** v1.3.2 b2: last error reported by AVSampleBufferDisplayLayer.error, if any. */
  layerError?: string | null;
  /** Present on broadcast events; identifies which transition fired. */
  event?: string;
}

export interface SendTestFrameOptions {
  /** Label rendered huge in the centre of the smoke card. Default "HOLD". */
  text?: string;
  /** "yellow" | "green" | "red" | "magenta" | "cyan" | "#RRGGBB". */
  background?: string;
}

export interface SendTestFrameResult {
  pushed: boolean;
  rendererMode: string;
  text: string;
  background: string;
  framesRendered: number;
  pipActive: boolean;
}

interface NexPip3DPlugin {
  isSupported(): Promise<{ supported: boolean; possible: boolean; reason?: string }>;
  prepare(): Promise<{ prepared: boolean; possible?: boolean; reason?: string }>;
  start(): Promise<{
    started: boolean;
    active?: boolean;
    reason?: string;
    diagnostics?: NexPip3DDiagnostics;
  }>;
  updateStatus(args: NexPip3DStatus): Promise<{ delivered: boolean; pipActive?: boolean }>;
  /**
   * v1.3.3(3): explicit clear of the clinical-HUD fields (circles, arrow,
   * bubble override). Older builds inferred a clear from any updateStatus
   * call that did not carry circles — that turned out to be the root cause
   * of the glass HUD reappearing during an active scan (legacy
   * `updatePipStatus()` calls were nuking the b10 fields). With this
   * method, callers wipe the clinical layout intentionally.
   */
  clearClinical(): Promise<{ cleared: boolean }>;
  /**
   * v1.3.3(4): enable / disable AVKit's automatic inline → PiP promotion.
   * The pre-b4 builds set this to `true` at controller construction and
   * left it on permanently, which made iOS auto-start PiP whenever the
   * user backgrounded the app (sign-in, home screen, Dex grid) before any
   * scan had been requested. Default is now OFF; the LiveScanModal flips
   * it ON only while a scan is actively running.
   */
  setAutoInline(args: { enabled: boolean }): Promise<{ autoInlineEnabled: boolean; controllerExists: boolean }>;
  /** Background-safe UIPasteboard write (naming session; see NexPip3D.swift). */
  setClipboardText(args: { text: string }): Promise<{ copied: boolean }>;
  stop(): Promise<{ stopped: boolean }>;
  getDiagnostics(): Promise<NexPip3DDiagnostics>;
  getStatus(): Promise<NexPip3DDiagnostics>;
  sendTestFrame(args: SendTestFrameOptions): Promise<SendTestFrameResult>;
  setRendererMode(args: { mode: "hud" | "smoke" }): Promise<{ rendererMode: string }>;
  addListener(
    eventName: "nexpip3d:state" | "nexpip3d:event" | "nexpip3d:action",
    listener: (data: unknown) => void,
  ): { remove: () => Promise<void> } | Promise<{ remove: () => Promise<void> }>;
}

interface CapacitorBridge {
  Plugins?: {
    NexPip3D?: NexPip3DPlugin;
  };
  isNativePlatform?: () => boolean;
}

function getCapacitor(): CapacitorBridge | null {
  if (typeof window === "undefined") return null;
  return (window as unknown as { Capacitor?: CapacitorBridge }).Capacitor ?? null;
}

function getPlugin(): NexPip3DPlugin | null {
  return getCapacitor()?.Plugins?.NexPip3D ?? null;
}

export interface SupportInfo {
  supported: boolean;
  possible: boolean;
  reason?: string;
}

/** True only on iOS where Capacitor has finished registering the plugin. */
export function isAvailable(): boolean {
  return !!getPlugin();
}

export async function isSupported(): Promise<SupportInfo> {
  const plugin = getPlugin();
  if (!plugin) {
    return {
      supported: false,
      possible: false,
      reason:
        getPlatform() === "web"
          ? "Nex PiP 3D only works in the Nexus Dex iOS app"
          : "NexPip3D plugin not available",
    };
  }
  try {
    const r = await plugin.isSupported();
    return { supported: !!r?.supported, possible: !!r?.possible, reason: r?.reason };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] isSupported failed", err);
    return { supported: false, possible: false, reason: "isSupported threw" };
  }
}

export async function prepare(): Promise<{ prepared: boolean; reason?: string }> {
  const plugin = getPlugin();
  if (!plugin) return { prepared: false, reason: "plugin not available" };
  try {
    const r = await plugin.prepare();
    return { prepared: !!r?.prepared, reason: r?.reason };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] prepare failed", err);
    return { prepared: false, reason: "prepare threw" };
  }
}

export interface StartResult {
  started: boolean;
  active?: boolean;
  reason?: string;
  diagnostics?: NexPip3DDiagnostics;
}

export async function start(): Promise<StartResult> {
  const plugin = getPlugin();
  if (!plugin) return { started: false, reason: "plugin not available" };
  try {
    const r = await plugin.start();
    return {
      started: !!r?.started,
      active: r?.active,
      reason: r?.reason,
      diagnostics: r?.diagnostics,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] start failed", err);
    return { started: false, reason: "start threw" };
  }
}

export async function updateStatus(status: NexPip3DStatus): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) {
    if (getPlatform() === "web") {
      // eslint-disable-next-line no-console
      console.info("[nexPip3D:web]", status);
      return true;
    }
    return false;
  }
  try {
    const r = await plugin.updateStatus(status);
    return !!r?.delivered;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] updateStatus failed", err);
    return false;
  }
}

/** Largest dex number rendered with 3-digit zero padding; beyond this the
 *  number widens naturally (e.g. #1010), matching the JS pad helper. */
const DEX_PAD_WIDTH = 3;

/** Zero-pad a dex number to at least 3 digits: 1 -> "001", 100 -> "100",
 *  1010 -> "1010". Exposed so the same padding is testable on the JS side. */
export function padDexNumber(n: number): string {
  const safe = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  return String(safe).padStart(DEX_PAD_WIDTH, "0");
}

/**
 * v1.3.3(4): toggle iOS AVKit auto-inline → PiP promotion. Default is
 * OFF since the controller is constructed; the LiveScanModal explicitly
 * flips it ON only while Start Live Scan has been tapped and a scan is
 * actually running, and OFF again when the scan ends. No-op on web.
 *
 * Older native binaries (pre-1.3.3 b4) do not export `setAutoInline`;
 * Capacitor's bridge throws on unknown methods. We guard against that so
 * the JS side degrades cleanly when running against a stale plugin.
 */
export async function setAutoInline(enabled: boolean): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;
  if (typeof (plugin as unknown as { setAutoInline?: unknown }).setAutoInline !== "function") {
    return false;
  }
  try {
    const r = await plugin.setAutoInline({ enabled: !!enabled });
    return !!r?.autoInlineEnabled === !!enabled;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] setAutoInline failed", err);
    return false;
  }
}

/**
 * Write text to the system pasteboard through the native plugin — the path
 * that still works while the app is BACKGROUNDED behind the PiP window (the
 * WebView clipboard needs focus). Same stale-binary guard as setAutoInline:
 * an older native build without the method degrades to false and the caller
 * falls back to the web clipboard.
 */
export async function setClipboardText(text: string): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;
  if (typeof (plugin as unknown as { setClipboardText?: unknown }).setClipboardText !== "function") {
    return false;
  }
  try {
    const r = await plugin.setClipboardText({ text });
    return !!r?.copied;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] setClipboardText failed", err);
    return false;
  }
}

export async function stop(): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;
  try {
    const r = await plugin.stop();
    return !!r?.stopped;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] stop failed", err);
    return false;
  }
}

/**
 * v1.3.3(3): wipe the clinical-HUD fields without tearing down PiP.
 * No-op on web. The native plugin no longer drops circles/arrow/bubble
 * implicitly when an `updateStatus` arrives without them, so callers that
 * want to leave the clinical layout (idle, finished, broadcast stopped)
 * call this explicitly.
 */
export async function clearClinical(): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;
  // Older native plugins (pre-1.3.3 b3) do not export clearClinical; the
  // Capacitor bridge throws on unknown methods. Guard it so the JS side
  // degrades cleanly on a stale binary.
  if (typeof (plugin as unknown as { clearClinical?: unknown }).clearClinical !== "function") {
    return false;
  }
  try {
    const r = await plugin.clearClinical();
    return !!r?.cleared;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] clearClinical failed", err);
    return false;
  }
}

/**
 * v1.3.2 b1: deterministic smoke-test frame. Switches the renderer into
 * `smoke` mode and pushes a static "huge label on solid colour" frame.
 * Used to prove the PiP video surface is visible before any OCR
 * coupling. Caller must `prepare()` and `start()` (or already have an
 * active PiP session) for the frame to appear in the floating window.
 */
export async function sendTestFrame(
  opts: SendTestFrameOptions = {},
): Promise<SendTestFrameResult | null> {
  const plugin = getPlugin();
  if (!plugin) {
    if (getPlatform() === "web") {
      // eslint-disable-next-line no-console
      console.info("[nexPip3D:web] sendTestFrame", opts);
    }
    return null;
  }
  try {
    const r = await plugin.sendTestFrame({
      text: opts.text ?? "HOLD",
      background: opts.background ?? "yellow",
    });
    return r;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] sendTestFrame failed", err);
    return null;
  }
}

/** v1.3.2 b1: flip renderer between full HUD and the smoke-test card. */
export async function setRendererMode(mode: "hud" | "smoke"): Promise<string | null> {
  const plugin = getPlugin();
  if (!plugin) return null;
  try {
    const r = await plugin.setRendererMode({ mode });
    return r.rendererMode;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] setRendererMode failed", err);
    return null;
  }
}

export async function getDiagnostics(): Promise<NexPip3DDiagnostics | null> {
  const plugin = getPlugin();
  if (!plugin) return null;
  try {
    const r = await plugin.getDiagnostics();
    return r;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[nexPip3D] getDiagnostics failed", err);
    return null;
  }
}

// ---------- Event subscriptions ----------

const _stateSubs = new Set<(d: NexPip3DDiagnostics) => void>();
/** HUD button taps (Android card, 2026-09-29): favourite / trade / open / menu. */
export interface NexPip3DAction {
  action: "favourite" | "trade" | "open" | "menu";
  recordId?: string;
}
const _actionSubs = new Set<(a: NexPip3DAction) => void>();
let _actionHandle: { remove: () => Promise<void> } | null = null;
const _eventSubs = new Set<(e: { event: string; snapshot: NexPip3DDiagnostics }) => void>();
let _stateHandle: { remove: () => Promise<void> } | null = null;
let _eventHandle: { remove: () => Promise<void> } | null = null;

let _listenersPromise: Promise<void> | null = null;

/**
 * Register the three native listeners once. Single-flight (2026-09-30):
 * two subscribers mounting in the same tick both saw the handles as null,
 * both awaited addListener, and every native action then reached each
 * subscriber TWICE. The Galaxy log showed each Star tap as two "favourite"
 * actions 1-58 ms apart, which toggled the record on and straight off.
 */
function ensureListeners(): Promise<void> {
  if (!_listenersPromise) {
    _listenersPromise = ensureListenersOnce().catch(() => {
      _listenersPromise = null;
    });
  }
  return _listenersPromise;
}

async function ensureListenersOnce(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin) return;
  if (!_stateHandle) {
    try {
      _stateHandle = await plugin.addListener("nexpip3d:state", (data) => {
        _stateSubs.forEach((cb) => {
          try {
            cb(data as NexPip3DDiagnostics);
          } catch {
            /* never let a subscriber crash the bridge */
          }
        });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[nexPip3D] addListener(nexpip3d:state) failed", err);
    }
  }
  if (!_eventHandle) {
    try {
      _eventHandle = await plugin.addListener("nexpip3d:event", (data) => {
        _eventSubs.forEach((cb) => {
          try {
            cb(data as { event: string; snapshot: NexPip3DDiagnostics });
          } catch {
            /* never let a subscriber crash the bridge */
          }
        });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[nexPip3D] addListener(nexpip3d:event) failed", err);
    }
  }
  if (!_actionHandle) {
    try {
      _actionHandle = await plugin.addListener("nexpip3d:action", (data) => {
        _actionSubs.forEach((cb) => {
          try {
            cb(data as NexPip3DAction);
          } catch {
            /* never let a subscriber crash the bridge */
          }
        });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[nexPip3D] addListener(nexpip3d:action) failed", err);
    }
  }
}

export function subscribeActions(cb: (a: NexPip3DAction) => void): () => void {
  _actionSubs.add(cb);
  void ensureListeners();
  return () => {
    _actionSubs.delete(cb);
  };
}

export function subscribeState(cb: (d: NexPip3DDiagnostics) => void): () => void {
  _stateSubs.add(cb);
  void ensureListeners();
  return () => {
    _stateSubs.delete(cb);
  };
}

export function subscribeEvents(
  cb: (e: { event: string; snapshot: NexPip3DDiagnostics }) => void,
): () => void {
  _eventSubs.add(cb);
  void ensureListeners();
  return () => {
    _eventSubs.delete(cb);
  };
}

